// Which records a document can be sent for signature FROM.
//
// CONSUMER: shared (Workers routes + web prep screen + tests).
//
// Grant, 2026-08-23: "build the send from opportunities and service
// records." The send engine and the archival rail were entity-neutral
// from the start; this is the short list of records the ROUTE layer
// accepts, and it lives here rather than in the route file because the
// prep screen needs the same vocabulary and the web bundle cannot
// import from workers/.
//
// HOUSEHOLD is the standalone lane (Grant, 2026-08-24): "all the
// functionality of creating an envelope should be a part of this
// [E-Sign page] ... make sure it is connected to a person record."
// Not every document a client signs belongs to a Processing record or
// an Opportunity — an engagement letter, a privacy notice, a consent
// form is simply between Stewardship and a household. Those had no
// send path at all, which meant they left the OS entirely (emailed as
// an attachment, signed in the provider's own console) and came back
// as an envelope with no record and no sender.
//
// CONTACT is here for the same reason, and it is not a duplicate of
// household (Grant, 2026-08-24: "select a document in the household or
// person or opportunity or processing or other record document list
// and then convert it to an e sign envelope").
//
// Documents genuinely live on BOTH: the OS files some things against a
// household and some against one person, and the Documents tab mounts
// on both pages. The send path's core invariant is that the document
// must live on the record you are sending FROM — so a document filed on
// Jane cannot be sent "as" her household without moving it. Rather than
// move a client's document to make a send work, a person is sendable in
// their own right and the envelope is filed exactly where the document
// already was.
//
// Grant's rule from 2026-08-23 — "every envelope needs to at least
// connect to a person which is also always connected to a household" —
// holds on both lanes: the household lane carries the person on the
// signer row, and the contact lane resolves the household from the
// person. Neither can produce an envelope attached to nothing.
//
// [History: this file briefly said the household was the anchor
// "rather than the contact", and a test asserted contacts could not
// send. That was written while household was the only standalone lane
// under consideration, and it was wrong for the reason above — it would
// have forced a document to move records to be signable.]
//
// USER and OTHER are the Stewardship Productions personnel lanes
// (Grant, 2026-09-28: "internal employee and contractor and onboarding
// stuff that needs to get esigned"). A new hire or a contractor is not
// a household, and making them one would count them as clients. So the
// person on these sends is a team member, not a contact:
//   * "user"  — a team member already in the OS (users.id). The signed
//     copy files in their own personnel file.
//   * "other" — ONLY with the Productions company id: the general
//     Productions personnel file, for someone not in the OS yet.
// Both names are the document entity types those files have always
// used, because the send's core invariant is that the document lives
// on the record you send from (documents.entity_type === kind). Who may
// touch them is decided in productions-access.ts, not here.
//
// Pure and dependency-free, same rule as field-types.ts.

export type EsignSendableKind =
  | "transaction"
  | "opportunity"
  | "service_record"
  | "household"
  | "contact"
  | "user"
  | "other";

export const ESIGN_SENDABLE_KINDS: readonly EsignSendableKind[] = [
  "transaction",
  "opportunity",
  "service_record",
  "household",
  "contact",
  "user",
  "other",
];

/** The Productions personnel lanes: a send whose signer is a member of
 *  the team (or someone about to be), never a client. */
export function isPersonnelKind(kind: EsignSendableKind): kind is "user" | "other" {
  return kind === "user" || kind === "other";
}

export function isEsignSendableKind(v: unknown): v is EsignSendableKind {
  return typeof v === "string" && (ESIGN_SENDABLE_KINDS as readonly string[]).includes(v);
}

/** What a record of this kind is called on screen, for the plain-English
 *  messages the send path returns ("No opportunity with that id"). */
export const ESIGN_SENDABLE_LABEL: Record<EsignSendableKind, string> = {
  transaction: "Processing record",
  opportunity: "opportunity",
  service_record: "service record",
  household: "household",
  contact: "contact",
  user: "team member",
  other: "Productions personnel file",
};

/**
 * Whether this kind carries its own company.
 *
 * A Processing record, an Opportunity and a service record each belong
 * to exactly one company, so the server reads it off the row. A
 * household does not — it can be served by five — so a standalone send
 * must be TOLD which company the document is for. That is not
 * bureaucracy: the company decides which saved layouts are offered and
 * whether the Realty commission-disclosure merge fields are available,
 * so guessing it would either hide a layout the team saved or withhold
 * a disclosure Realty owes.
 */
export function kindCarriesItsOwnCompany(kind: EsignSendableKind): boolean {
  // The personnel lanes carry theirs too: always Stewardship Productions.
  return kind !== "household" && kind !== "contact";
}
